import React, { useState, useEffect, useCallback } from 'react';
import { apiService, RawOrderRow, OrdersTableQuery } from '../services/api';
import Modal from './Modal';
import './OrdersTable.css';

// Fields that should not be editable (system fields and complex JSON)
const NON_EDITABLE_FIELDS = new Set([
    'id',
    'created_at',
    'updated_at',
    'items_json',
    'payment_metadata_json',
    'client_metadata_json',
]);

interface EditingCell {
    orderId: string;
    column: keyof RawOrderRow;
    currentValue: unknown;
}

const OrdersTable: React.FC = () => {
    const [rows, setRows] = useState<RawOrderRow[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Filter state
    const [limit, setLimit] = useState(30);
    const [orderDateFrom, setOrderDateFrom] = useState('');
    const [orderDateTo, setOrderDateTo] = useState('');
    const [deliveryDateFrom, setDeliveryDateFrom] = useState('');
    const [deliveryDateTo, setDeliveryDateTo] = useState('');

    // Cell editing state
    const [editingCell, setEditingCell] = useState<EditingCell | null>(null);
    const [editValue, setEditValue] = useState('');
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);

    const fetchOrders = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const query: OrdersTableQuery = { limit };
            if (orderDateFrom) query.orderDateFrom = orderDateFrom;
            if (orderDateTo) query.orderDateTo = orderDateTo;
            if (deliveryDateFrom) query.deliveryDateFrom = deliveryDateFrom;
            if (deliveryDateTo) query.deliveryDateTo = deliveryDateTo;

            const result = await apiService.getOrdersTable(query);
            setRows(result.rows);
            setTotalCount(result.totalCount);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to fetch orders');
        } finally {
            setLoading(false);
        }
    }, [limit, orderDateFrom, orderDateTo, deliveryDateFrom, deliveryDateTo]);

    useEffect(() => {
        fetchOrders();
    }, [fetchOrders]);

    // Handle double-click to edit a cell
    const handleCellDoubleClick = (row: RawOrderRow, column: keyof RawOrderRow) => {
        if (NON_EDITABLE_FIELDS.has(column)) {
            return; // Don't allow editing non-editable fields
        }
        
        const currentValue = row[column];
        setEditingCell({
            orderId: row.id,
            column,
            currentValue,
        });
        setEditValue(currentValue === null || currentValue === undefined ? '' : String(currentValue));
        setSaveError(null);
    };

    // Close the edit modal
    const handleCloseEdit = () => {
        setEditingCell(null);
        setEditValue('');
        setSaveError(null);
    };

    // Save the edited value
    const handleSaveEdit = async () => {
        if (!editingCell) return;

        setSaving(true);
        setSaveError(null);

        try {
            // Convert value back to appropriate type if needed
            let valueToSave: unknown = editValue;
            
            // Handle numeric fields
            const numericFields = new Set([
                'zone', 'is_postcode_found', 'subtotal', 'total', 'deliver_now',
                'slot_was_bumped', 'driver_tip_added', 'driver_tip_amount',
                'receive_email_updates', 'is_test', 'is_paid', 'refund_amount',
                'discount_percentage', 'discount_fixed_amount', 'discount_amount'
            ]);
            
            if (numericFields.has(editingCell.column)) {
                if (editValue === '' || editValue === 'null') {
                    valueToSave = null;
                } else {
                    const num = Number(editValue);
                    if (!isNaN(num)) {
                        valueToSave = num;
                    }
                }
            } else if (editValue === '' || editValue === 'null') {
                valueToSave = null;
            }

            await apiService.updateOrderField(editingCell.orderId, editingCell.column, valueToSave);
            
            // Refresh the table data
            await fetchOrders();
            handleCloseEdit();
        } catch (err) {
            setSaveError(err instanceof Error ? err.message : 'Failed to save');
        } finally {
            setSaving(false);
        }
    };

    // Get all column names from first row, or use a default list
    const columns: (keyof RawOrderRow)[] = rows.length > 0
        ? (Object.keys(rows[0]) as (keyof RawOrderRow)[])
        : [
            'id', 'customer_name', 'customer_email', 'customer_phone', 'address',
            'postcode', 'number', 'street', 'area', 'zone', 'is_postcode_found', 'address_id',
            'items_json', 'subtotal', 'total', 'status', 'order_date', 'delivery_date',
            'deliver_now', 'delivery_notes', 'scheduled_slot', 'slot_was_bumped',
            'original_requested_slot', 'payment_method', 'driver_tip_added', 'driver_tip_amount',
            'receive_email_updates', 'is_test', 'is_paid', 'refund_amount',
            'sumup_checkout_id', 'paypal_order_id', 'paypal_capture_id',
            'payment_metadata_json', 'client_metadata_json',
            'created_at', 'updated_at', 'discount_code', 'discount_percentage',
            'discount_fixed_amount', 'discount_amount'
        ];

    const formatCellValue = (value: unknown): string => {
        if (value === null || value === undefined) return '';
        if (typeof value === 'boolean') return value ? 'Yes' : 'No';
        if (typeof value === 'number') return value.toString();
        if (typeof value === 'string') {
            // Truncate long strings for display
            if (value.length > 100) return value.substring(0, 100) + '...';
            return value;
        }
        return JSON.stringify(value);
    };

    return (
        <div className="orders-table-container">
            <h1>📊 Orders Table</h1>
            <p className="subtitle">Raw database view with all columns</p>

            {/* Filters */}
            <div className="filters-section">
                <div className="filter-row">
                    <div className="filter-group">
                        <label>Limit:</label>
                        <select value={limit} onChange={(e) => setLimit(Number(e.target.value))}>
                            <option value={10}>10</option>
                            <option value={30}>30</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                            <option value={200}>200</option>
                            <option value={500}>500</option>
                        </select>
                    </div>

                    <div className="filter-group">
                        <label>Order Date From:</label>
                        <input
                            type="date"
                            value={orderDateFrom}
                            onChange={(e) => setOrderDateFrom(e.target.value)}
                        />
                    </div>

                    <div className="filter-group">
                        <label>Order Date To:</label>
                        <input
                            type="date"
                            value={orderDateTo}
                            onChange={(e) => setOrderDateTo(e.target.value)}
                        />
                    </div>

                    <div className="filter-group">
                        <label>Delivery Date From:</label>
                        <input
                            type="date"
                            value={deliveryDateFrom}
                            onChange={(e) => setDeliveryDateFrom(e.target.value)}
                        />
                    </div>

                    <div className="filter-group">
                        <label>Delivery Date To:</label>
                        <input
                            type="date"
                            value={deliveryDateTo}
                            onChange={(e) => setDeliveryDateTo(e.target.value)}
                        />
                    </div>

                    <button
                        className="clear-filters-btn"
                        onClick={() => {
                            setOrderDateFrom('');
                            setOrderDateTo('');
                            setDeliveryDateFrom('');
                            setDeliveryDateTo('');
                        }}
                    >
                        Clear Filters
                    </button>
                </div>
            </div>

            {/* Stats */}
            <div className="stats-bar">
                <span>Showing: <strong>{rows.length}</strong> of <strong>{totalCount}</strong> orders</span>
                {loading && <span className="loading-indicator">Loading...</span>}
            </div>

            {/* Error */}
            {error && <div className="error-message">{error}</div>}

            {/* Table */}
            <div className="table-wrapper">
                <table className="orders-table">
                    <thead>
                        <tr>
                            {columns.map((col) => (
                                <th key={col}>{col}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => (
                            <tr key={row.id}>
                                {columns.map((col) => (
                                    <td 
                                        key={col} 
                                        title={String(row[col] ?? '')}
                                        onDoubleClick={() => handleCellDoubleClick(row, col)}
                                        className={NON_EDITABLE_FIELDS.has(col) ? '' : 'editable-cell'}
                                    >
                                        {formatCellValue(row[col])}
                                    </td>
                                ))}
                            </tr>
                        ))}
                        {rows.length === 0 && !loading && (
                            <tr>
                                <td colSpan={columns.length} className="no-data">
                                    No orders found
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Edit Cell Modal */}
            <Modal
                isOpen={editingCell !== null}
                onClose={handleCloseEdit}
                showCloseButton={true}
                className="edit-cell-modal"
            >
                <div className="edit-cell-content">
                    <h3>Edit Field</h3>
                    {editingCell && (
                        <>
                            <div className="edit-field-info">
                                <span className="field-label">Field:</span>
                                <span className="field-name">{editingCell.column}</span>
                            </div>
                            <div className="edit-field-info">
                                <span className="field-label">Order ID:</span>
                                <span className="field-value">{editingCell.orderId}</span>
                            </div>
                            <div className="edit-field-info">
                                <span className="field-label">Current Value:</span>
                                <span className="field-value">
                                    {editingCell.currentValue === null ? '(null)' : String(editingCell.currentValue)}
                                </span>
                            </div>
                            <div className="edit-input-group">
                                <label htmlFor="edit-value">New Value:</label>
                                <textarea
                                    id="edit-value"
                                    value={editValue}
                                    onChange={(e) => setEditValue(e.target.value)}
                                    rows={3}
                                    disabled={saving}
                                    autoFocus
                                />
                                <small className="edit-hint">Type "null" or leave empty for null value</small>
                            </div>
                            {saveError && (
                                <div className="edit-error">{saveError}</div>
                            )}
                            <div className="edit-actions">
                                <button
                                    type="button"
                                    className="cancel-btn"
                                    onClick={handleCloseEdit}
                                    disabled={saving}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="save-btn"
                                    onClick={handleSaveEdit}
                                    disabled={saving}
                                >
                                    {saving ? 'Saving...' : 'Save'}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </Modal>
        </div>
    );
};

export default OrdersTable;
